import { Injectable, inject } from '@angular/core';
import { HttpParams, HttpErrorResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  IngestedOrder,
  ReceivedOrder
} from '@model/ingested-order/ingested-order.model';
import {
  FilterOptionsMapper,
  Region,
  Site,
  ExamType,
  RegionalBackendDto,
  SiteBackendDto,
  ExamTypeBackendDto
} from '@model/filter/filter-options.model';
import {
  IngestedOrdersQueryParams,
  OrderStatus
} from '../ingested-orders/api/ingested-orders-api.interface';
import {
  IngestedOrderBackendResponse,
  IngestedOrdersMapper,
  ReceivedOrderBackendResponse,
  ReceivedOrdersMapper
} from '../ingested-orders/mappers/ingested-orders.mapper';
import { AppHttpClientApi } from '../../helpers/api/http-client-api';
import { HttpErrorHandlerService } from '@core/error-handling/http-error-handler.service';

/**
 * Generic service for managing orders (both ingested and received)
 * Handles business logic, data transformation, and HTTP communication
 *
 * Architecture:
 * - Single service for all order statuses (INGRESADA, RECIBIDA)
 * - Uses AppHttpClientApi for all HTTP operations
 * - Transforms backend DTOs to domain models based on status
 * - Provides business-level interface for components
 */
@Injectable({
  providedIn: 'root'
})
export class OrdersService {
  private readonly httpClient = inject(AppHttpClientApi);
  private readonly errorHandler = inject(HttpErrorHandlerService);
  private readonly baseUrl = 'admitted-orders';

  /**
   * Get paginated list of orders
   * Automatically uses correct mapper based on status
   * @param params Query parameters including status (INGRESADA or RECIBIDA)
   */
  getOrders(params?: IngestedOrdersQueryParams): Observable<{
    orders: IngestedOrder[] | ReceivedOrder[];
    pagination: {
      page: number;
      size: number;
      total: number;
      totalPages: number;
    };
  }> {
    const status = params?.status ?? OrderStatus.INGRESADA;
    const endpoint = this.determineEndpoint(params);
    const httpParams = this.buildHttpParams(params);

    // Use different backend response type and mapper based on status
    if (status === OrderStatus.RECIBIDA) {
      return this.httpClient
        .getRaw<ReceivedOrderBackendResponse>(endpoint, { params: httpParams })
        .pipe(
          map((response) => ({
            orders: ReceivedOrdersMapper.fromBackendDtoArray(response.content),
            pagination: {
              page: response.pageableInfo.page,
              size: response.pageableInfo.size,
              total: response.pageableInfo.totalElements,
              totalPages: response.pageableInfo.totalPages
            }
          })),
          catchError((error: HttpErrorResponse) =>
            this.errorHandler.handleError('getOrders', error)
          )
        );
    }

    // Default: INGRESADA status
    return this.httpClient
      .getRaw<IngestedOrderBackendResponse>(endpoint, { params: httpParams })
      .pipe(
        map((response) => ({
          orders: IngestedOrdersMapper.fromBackendDtoArray(response.content),
          pagination: {
            page: response.pageableInfo.page,
            size: response.pageableInfo.size,
            total: response.pageableInfo.totalElements,
            totalPages: response.pageableInfo.totalPages
          }
        })),
        catchError((error: HttpErrorResponse) =>
          this.errorHandler.handleError('getOrders', error)
        )
      );
  }

  /**
   * GET /preanalytical/master-data/regionals
   * Fetches available regions
   */
  getRegions(): Observable<Region[]> {
    return this.httpClient
      .getRaw<RegionalBackendDto[]>('master-data/regionals')
      .pipe(
        map((dtos) => FilterOptionsMapper.mapRegionals(dtos)),
        catchError((error: HttpErrorResponse) =>
          this.errorHandler.handleError('getRegions', error)
        )
      );
  }

  /**
   * GET /preanalytical/master-data/sites
   * Fetches available sites
   */
  getSites(): Observable<Site[]> {
    return this.httpClient.getRaw<SiteBackendDto[]>('master-data/sites').pipe(
      map((dtos) => FilterOptionsMapper.mapSites(dtos)),
      catchError((error: HttpErrorResponse) =>
        this.errorHandler.handleError('getSites', error)
      )
    );
  }

  /**
   * GET /preanalytical/master-data/exam-types
   * Fetches available exam types
   */
  getExamTypes(): Observable<ExamType[]> {
    return this.httpClient
      .getRaw<ExamTypeBackendDto[]>('master-data/exam-types')
      .pipe(
        map((dtos) => FilterOptionsMapper.mapExamTypes(dtos)),
        catchError((error: HttpErrorResponse) =>
          this.errorHandler.handleError('getExamTypes', error)
        )
      );
  }

  /**
   * GET /preanalitica/master-data/diagnoses
   * Fetches available diagnoses
   */
  getDiagnoses(): Observable<{ id: string; name: string }[]> {
    return this.httpClient
      .getRaw<{ id: string; name: string }[]>('master-data/diagnoses')
      .pipe(
        catchError((error: HttpErrorResponse) =>
          this.errorHandler.handleError('getDiagnoses', error)
        )
      );
  }

  /**
   * GET /preanalitica/master-data/specialists
   * Fetches available specialists
   */
  getSpecialists(): Observable<{ id: string; name: string }[]> {
    return this.httpClient
      .getRaw<{ id: string; name: string }[]>('master-data/specialists')
      .pipe(
        catchError((error: HttpErrorResponse) =>
          this.errorHandler.handleError('getSpecialists', error)
        )
      );
  }

  /**
   * GET /preanalitica/master-data/reprocess-causes
   * Fetches available reprocess causes
   */
  getReprocessCauses(): Observable<{ id: string; name: string }[]> {
    return this.httpClient
      .getRaw<{ id: string; name: string }[]>('master-data/reprocess-causes')
      .pipe(
        catchError((error: HttpErrorResponse) =>
          this.errorHandler.handleError('getReprocessCauses', error)
        )
      );
  }

  /**
   * GET /preanalitica/master-data/reprocess-statuses
   * Fetches available reprocess statuses
   */
  getReprocessStatuses(): Observable<{ id: string; name: string }[]> {
    return this.httpClient
      .getRaw<{ id: string; name: string }[]>('master-data/reprocess-statuses')
      .pipe(
        catchError((error: HttpErrorResponse) =>
          this.errorHandler.handleError('getReprocessStatuses', error)
        )
      );
  }

  getSpecimenContainers(): Observable<{ id: string; name: string }[]> {
    return this.httpClient
      .getRaw<{ id: string; name: string }[]>('master-data/specimen-containers')
      .pipe(
        catchError((error: HttpErrorResponse) =>
          this.errorHandler.handleError('getSpecimenContainers', error)
        )
      );
  }

  getProviders(): Observable<{ id: string; name: string }[]> {
    return this.httpClient
      .getRaw<{ id: string; name: string }[]>('master-data/providers')
      .pipe(
        catchError((error: HttpErrorResponse) =>
          this.errorHandler.handleError('getProviders', error)
        )
      );
  }

  getInsuranceCompanies(): Observable<{ id: string; name: string }[]> {
    return this.httpClient
      .getRaw<{ id: string; name: string }[]>('master-data/insurance-companies')
      .pipe(
        catchError((error: HttpErrorResponse) =>
          this.errorHandler.handleError('getInsuranceCompanies', error)
        )
      );
  }

  /**
   * GET /preanalitica/master-data/priorities
   * Fetches available priorities
   */
  getPriorities(): Observable<{ id: string; name: string }[]> {
    return this.httpClient
      .getRaw<{ id: string; name: string }[]>('master-data/priorities')
      .pipe(
        catchError((error: HttpErrorResponse) =>
          this.errorHandler.handleError('getPriorities', error)
        )
      );
  }

  /**
   * GET /preanalitica/master-data/laboratories
   * Fetches available laboratories
   */
  getLaboratories(): Observable<{ id: string; name: string }[]> {
    return this.httpClient
      .getRaw<{ id: string; name: string }[]>('master-data/laboratories')
      .pipe(
        catchError((error: HttpErrorResponse) =>
          this.errorHandler.handleError('getLaboratories', error)
        )
      );
  }

  /**
   * GET /preanalitica/admitted-orders/export?status={status}
   * Export orders to CSV file
   * Builds URL manually to support multiple filterField/filterValue pairs in correct order
   */
  exportOrders(params?: IngestedOrdersQueryParams): Observable<Blob> {
    const queryString = this.buildExportQueryString(params);
    const url = `${this.baseUrl}/export?${queryString}`;

    return this.httpClient
      .getBlob(url)
      .pipe(
        catchError((error: HttpErrorResponse) =>
          this.errorHandler.handleError('Export Orders', error)
        )
      );
  }

  /**
   * Determines which endpoint to use based on query parameters
   * Uses filter endpoint when filters are present, otherwise uses regular endpoint
   */
  private determineEndpoint(params?: IngestedOrdersQueryParams): string {
    const hasFilters = Boolean(
      (params?.priority && params.priority.length > 0) ??
        (params?.region && params.region.length > 0) ??
        (params?.sites && params.sites.length > 0) ??
        (params?.examType && params.examType.length > 0) ??
        (params?.reprocessCauses && params.reprocessCauses.length > 0) ??
        params?.startDate ??
        params?.endDate ??
        false
    );

    return hasFilters ? 'filter-options/filter' : this.baseUrl;
  }

  /**
   * Builds HttpParams from query parameters
   * Handles pagination, sorting, searching, and filtering
   * Default sorting: admissionDate DESC
   */
  private buildHttpParams(params?: IngestedOrdersQueryParams): HttpParams {
    const status = params?.status ?? OrderStatus.INGRESADA;
    let httpParams = new HttpParams().set('status', status);

    // Add search parameters FIRST (after status)
    if (params?.searchField && params?.searchValue) {
      httpParams = httpParams
        .set('searchField', params.searchField)
        .set('searchValue', params.searchValue);
    }

    // Add filter parameters
    httpParams = this.addFilterParams(httpParams, params);

    // Add pagination
    httpParams = httpParams
      .set('page', (params?.page ?? 0).toString())
      .set('size', (params?.size ?? 10).toString());

    // Add sorting - default to admissionDate DESC
    const sortBy = params?.sortBy ?? 'admissionDate';
    const sortDirection = params?.sortDirection ?? 'DESC';
    httpParams = httpParams
      .set('sortBy', sortBy)
      .set('sortDirection', sortDirection);

    return httpParams;
  }

  /**
   * Builds query string for export endpoint
   * Uses same parameter names as regular endpoint (regionIds, siteCodes, examId, etc.)
   * Multiple values for same parameter are comma-separated
   * Order: status -> filters -> search -> sorting
   */
  private buildExportQueryString(params?: IngestedOrdersQueryParams): string {
    const status = params?.status ?? OrderStatus.INGRESADA;

    // Build query string manually
    const queryParts: string[] = [`status=${status}`];

    // Add array-based filters FIRST
    // Multiple values are comma-separated
    const arrayFilterMapping = [
      { paramKey: 'priority', queryParamName: 'priorityIds' },
      { paramKey: 'region', queryParamName: 'regionIds' },
      { paramKey: 'sites', queryParamName: 'siteCodes' },
      { paramKey: 'examType', queryParamName: 'examId' },
      { paramKey: 'reprocessCauses', queryParamName: 'reprocessCauseIds' }
    ];

    arrayFilterMapping.forEach(({ paramKey, queryParamName }) => {
      const values = params?.[paramKey as keyof IngestedOrdersQueryParams];
      if (Array.isArray(values) && values.length > 0) {
        // Join multiple values with comma
        const commaSeparatedValues = values
          .map((v) => encodeURIComponent(v))
          .join(',');
        queryParts.push(`${queryParamName}=${commaSeparatedValues}`);
      }
    });

    // Add date filters
    if (params?.startDate instanceof Date) {
      const dateStart = params.startDate.toISOString().split('T')[0];
      queryParts.push(`dateStart=${encodeURIComponent(dateStart)}`);
    }

    if (params?.endDate instanceof Date) {
      const dateEnd = params.endDate.toISOString().split('T')[0];
      queryParts.push(`dateEnd=${encodeURIComponent(dateEnd)}`);
    }

    // Add search parameters AFTER filters
    if (params?.searchField && params?.searchValue) {
      queryParts.push(`searchField=${encodeURIComponent(params.searchField)}`);
      queryParts.push(`searchValue=${encodeURIComponent(params.searchValue)}`);
    }

    // Add sorting - default to admissionDate DESC if not specified
    const sortBy = params?.sortBy ?? 'admissionDate';
    const sortDirection = params?.sortDirection ?? 'DESC';
    queryParts.push(`sortBy=${encodeURIComponent(sortBy)}`);
    queryParts.push(`sortDirection=${sortDirection}`);

    return queryParts.join('&');
  }

  /**
   * Adds filter parameters to HttpParams
   * Handles priority, region, sites, examType, and date range filters
   */
  private addFilterParams(
    httpParams: HttpParams,
    params?: IngestedOrdersQueryParams
  ): HttpParams {
    if (!params) {
      return httpParams;
    }

    // Array filters mapping - property name to query param name
    const arrayFilterMapping = [
      { paramKey: 'priority', queryParamName: 'priorityIds' },
      { paramKey: 'region', queryParamName: 'regionIds' },
      { paramKey: 'sites', queryParamName: 'siteCodes' },
      { paramKey: 'examType', queryParamName: 'examId' },
      { paramKey: 'reprocessCauses', queryParamName: 'reprocessCauseIds' }
    ];

    // Process array-based filters using append for multiple values
    arrayFilterMapping.forEach(({ paramKey, queryParamName }) => {
      const values = params[paramKey as keyof IngestedOrdersQueryParams];
      if (Array.isArray(values) && values.length > 0) {
        values.forEach((value) => {
          httpParams = httpParams.append(queryParamName, value);
        });
      }
    });

    // Date filters mapping
    const dateFilterMapping = [
      { paramKey: 'startDate', queryParamName: 'dateStart' },
      { paramKey: 'endDate', queryParamName: 'dateEnd' }
    ];

    // Process date filters - convert to ISO format
    dateFilterMapping.forEach(({ paramKey, queryParamName }) => {
      const date = params[paramKey as keyof IngestedOrdersQueryParams];
      if (date instanceof Date) {
        httpParams = httpParams.set(
          queryParamName,
          date.toISOString().split('T')[0]
        );
      }
    });

    return httpParams;
  }
}
